import { format } from "date-fns";
import { FC, useMemo, useState } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";

import { Calendar } from "shared/assets/icons";
import { FieldWrapper, FieldWrapperProps } from "shared/components";
import { BreakpointSize, colors } from "shared/styles/constants";
import { IFormFieldStyle } from "shared/types";
import { DateFormat, useBreakpoint } from "shared/utils";
import { InputFieldWrapper } from "../styles";
import {
  DatePickerPrefixIcon,
  SimpleDateTimeFieldContainer,
  StyledCalendarComponent,
  StyledPopperContainer,
  StyledReactDatePicker,
} from "./styles";

import "react-datepicker/dist/react-datepicker.css";

interface DatePickerFieldProps extends FieldWrapperProps {
  control: Control;
  dateFormat?: DateFormat;
  validation?: RegisterOptions;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  defaultValue?: string;
  style?: IFormFieldStyle;
  minDate?: Date;
  onChange?: (e: any) => void;
  onClick?: () => void;
}

export const DatePickerField: FC<DatePickerFieldProps> = ({
  control,
  name,
  validation,
  defaultValue,
  error,
  disabled,
  dateFormat,
  style,
  onChange: handleChange,
  minDate,
  tabIndex,
  ...rest
}) => {
  const isMobile = useBreakpoint(BreakpointSize.l);

  const datePickerFormat = useMemo(() => {
    return (dateFormat || DateFormat.dmy).replaceAll("m", "M");
  }, [dateFormat]);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  return (
    <SimpleDateTimeFieldContainer style={style}>
      <Controller
        control={control}
        name={name}
        rules={validation}
        defaultValue={defaultValue}
        render={({ onChange, value }) => {
          return (
            <FieldWrapper name={name} error={error} {...rest}>
              <InputFieldWrapper
                isDisabled={disabled}
                error={!!error}
                onKeyDown={(e) => {
                  // Weird fix for Safari where tabbing a datepicker field doesn't work properly smh.
                  if (e.code === "Tab") {
                    setIsOpen(false);

                    const nextIndex = e.shiftKey
                      ? (tabIndex || 0) - 1
                      : (tabIndex || 0) + 1;

                    const nextElement = document.querySelector(
                      `input[tabindex="${nextIndex}"]`
                    ) as HTMLElement;

                    if (nextElement) {
                      e.preventDefault();
                      // Safari stuff 🤦‍♂️
                      setTimeout(() => {
                        nextElement?.focus();
                      }, 0);
                    }
                  }
                }}
              >
                <DatePickerPrefixIcon
                  as={Calendar}
                  color={colors.grey4}
                  size="xl"
                />
                <StyledReactDatePicker
                  dateFormat={datePickerFormat}
                  onFocus={() => setIsOpen(true)}
                  onInputClick={() => setIsOpen(true)}
                  onClickOutside={() => setIsOpen(false)}
                  onChange={(date: Date) => {
                    if (date) {
                      const formattedDate = format(date, "yyyy-MM-dd");
                      onChange(formattedDate);
                      handleChange && handleChange(formattedDate);
                    } else {
                      onChange(null);
                      handleChange && handleChange(null);
                    }

                    setIsOpen(false);
                  }}
                  id={name}
                  minDate={minDate}
                  dropdownMode="select"
                  selected={
                    value
                      ? new Date(`${value}T00:00:00`)
                      : defaultValue
                      ? new Date(defaultValue)
                      : null
                  }
                  placeholderText={dateFormat || DateFormat.dmy}
                  showPopperArrow={false}
                  popperPlacement="bottom-start"
                  calendarContainer={StyledCalendarComponent}
                  popperContainer={StyledPopperContainer}
                  withPortal={isMobile}
                  autoComplete="off"
                  tabIndex={tabIndex}
                  open={isOpen}
                  calendarStartDay={1}
                />
              </InputFieldWrapper>
            </FieldWrapper>
          );
        }}
      />
    </SimpleDateTimeFieldContainer>
  );
};
